<script setup lang="ts">
import { PERMISSIONS } from '~/constants/permissions';
import type { UserNotification } from '~/composables/useNotifications';

const props = defineProps<{ notification: UserNotification }>();
const emit = defineEmits<{ select: [] }>();

const { label, markAsRead, remove } = useNotifications();
const { hasPermission } = usePermissions();

// The API rejects removing a row that is still unread, and authorizes the call
// against user.notification.destroy — hide the control rather than let it 403.
const canRemove = computed(
  () =>
    Boolean(props.notification.read_at) &&
    hasPermission(PERMISSIONS.USER_NOTIFICATION.DESTROY)
);

const open = async () => {
  emit('select');

  await markAsRead(props.notification);
};
</script>

<template>
  <li
    class="list-group-item"
    :class="{ 'notification-unread': !notification.read_at }"
  >
    <div class="d-flex align-items-start justify-content-between">
      <div class="notification-text" @click="open">
        <div class="notification-icon me-2">
          <span
            class="notification-icon-symbol"
            :class="notification.data.icon_style ?? 'standard'"
          >
            <i :class="notification.data.icon ?? 'fas fa-bell'" />
          </span>
        </div>
        <div>
          <div class="heading">{{ label(notification.data.title) }}</div>
          <p class="notification-body mb-1">
            {{ label(notification.data.description) }}
          </p>
          <div class="notification-time text-muted">
            {{ notification.created_at_human }}
          </div>
        </div>
      </div>
      <i
        v-if="canRemove"
        class="fa fa-solid fa-xmark text-muted remove-notification"
        :title="$t('notifications.buttons.remove')"
        :aria-label="$t('notifications.buttons.remove')"
        @click.stop="remove(notification)"
      />
    </div>
  </li>
</template>

<style scoped>
/* Bootstrap's own padding is the only thing keeping the row off the panel edges, and
   it leaves the remove control sitting almost against it — give the row its own. */
.list-group-item {
  padding: 0.5rem 1rem;
  border: 0;
}

/* Unread rows are tinted with an accent; read rows sit flat. The title stays bold in
   both so it is never mistaken for the message below it. */
.notification-unread {
  background-color: var(--dropdown-custom-list-bg-active);
  border-left: 3px solid var(--color-primary);
}

.heading {
  color: #252c35;
}

/* The message is the secondary line: smaller and softer than the title above it. */
.notification-body {
  font-size: 13px;
  line-height: 1.35;
  color: #595b60;
}

/* The shared .notification-text is `flex: 0 0 100%`, sized for a row with nothing
   beside it. With the remove control next to it that overflows the panel width, so
   let it shrink and wrap instead. */
.notification-text {
  flex: 1 1 auto;
  min-width: 0;
}

.heading,
.notification-body {
  overflow-wrap: anywhere;
}

.remove-notification {
  padding-left: 0.75rem;
  cursor: pointer;
  transition: color 0.3s;
}

.remove-notification:hover {
  color: #000;
}
</style>
